import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EvaluationService } from '@app-services/evaluation.service';
import { FileUploaderService } from '@app-services/file-uploader.service';
import { FileUploadResult, OemDealerService } from '@app-services/oem-dealer.service';
import { StringUtils } from '@app-utils/StringUtils';
import { environment } from '@env/environment';
import * as moment from 'moment';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { NgxGalleryComponent, NgxGalleryImage, NgxGalleryOptions } from 'ngx-gallery-images-video';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-dialog-upload-report-item',
  templateUrl: './dialog-upload-report-item.component.html',
  styleUrls: ['./dialog-upload-report-item.component.scss']
})
export class DialogUploadReportItemComponent implements OnInit, OnDestroy {

  title: string;
  reportEquipment: string;
  vehicleId: string;
  onlyView: boolean;
  /** 'photo' = tirar foto, 'video' = gravar vídeo */
  mediaType: 'photo' | 'video' = 'photo';
  photosAndVideos: { url: string, isVideo: boolean }[] = [];
  imgUploader$: Observable<FileUploader>;
  imgUploadMessage: string;
  imgUploadError: boolean;
  /** Nome do arquivo e progresso para exibir no file-upload-item (igual ao formulário de avaliação) */
  uploadFileName: string;
  uploadProgress: number = 0;

  /** Mobile: usar câmera nativa (input capture). Desktop: preview no navegador. */
  isMobile = false;

  /** Câmera / gravação (desktop) */
  stream: MediaStream | null = null;
  cameraActive = false;
  isRecording = false;
  mediaRecorder: MediaRecorder | null = null;
  recordedChunks: Blob[] = [];
  isUploading = false;
  cameraError: string | null = null;

  galleryVisible = false;
  galleryOptions: NgxGalleryOptions[] = [];
  galleryImages: NgxGalleryImage[] = [];
  /** Referência ao uploader para disparar upload ao adicionar arquivo (Selecionar arquivo). */
  uploaderRef: FileUploader | null = null;

  @ViewChild('onlyPreviewGallery') onlyPreviewGallery: NgxGalleryComponent;
  @ViewChild('cameraPreview') cameraPreview: ElementRef<HTMLVideoElement>;

  constructor(
    private readonly dialogRef: MatDialogRef<DialogUploadReportItemComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly fileUploaderService: FileUploaderService,
    private readonly evaluationService: EvaluationService,
    private readonly oemDealerService: OemDealerService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.reportEquipment = data.reportEquipment;
    this.title = data.title;
    this.vehicleId = data.vehicleId;
    this.onlyView = data.onlyView;
    this.mediaType = data.mediaType === 'video' ? 'video' : 'photo';

    const mapped = (data.photosAndVideos || []).map((item: string) => ({
      url: item,
      isVideo: StringUtils.isVideoUrl(item)
    }));
    // Exibir apenas o tipo correspondente ao modal: fotos no modal de foto, vídeos no modal de vídeo
    this.photosAndVideos = this.mediaType === 'video'
      ? mapped.filter(m => m.isVideo)
      : mapped.filter(m => !m.isVideo);

    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    this.initReportFileUploader();
  }

  ngOnInit() {
    this.galleryOptions = [
      {
        image: false,
        thumbnails: false,
        width: '0px',
        height: '0px',
      }
    ];

    this.galleryImages = this.photosAndVideos
      .map(media => ({
        small: StringUtils.getMediaSrc(media.url),
        medium: StringUtils.getMediaSrc(media.url),
        big: StringUtils.getMediaSrc(media.url)
      }));

    this.imgUploader$.subscribe(uploader => {
      this.uploaderRef = uploader;
      this.cdr.detectChanges();
    });
  }

  @HostListener('window:keyup.esc')
  onNoClick(): void {
    this.dialogRef.close();
  }

  confirm() {
    this.stopCamera();
    const photoAndVideosUrls = this.photosAndVideos.map(item => item.url);
    this.dialogRef.close({ photosAndVideos: photoAndVideosUrls });
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  /** Inicia a câmera e exibe o preview. */
  async startCamera(): Promise<void> {
    this.cameraError = null;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: this.mediaType === 'video'
      });
      this.cameraActive = true;
      setTimeout(() => this.attachStreamToVideo(), 100);
    } catch (err: any) {
      this.cameraError = err?.message || 'Não foi possível acessar a câmera. Verifique as permissões.';
    }
  }

  private attachStreamToVideo(): void {
    const video = this.cameraPreview?.nativeElement;
    if (video && this.stream) {
      video.srcObject = this.stream;
      video.play().catch(() => {});
    }
  }

  /** Para a câmera e libera os dispositivos. */
  stopCamera(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }
    this.recordedChunks = [];
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
    this.cameraActive = false;
    this.isRecording = false;
    const video = this.cameraPreview?.nativeElement;
    if (video) {
      video.srcObject = null;
    }
  }

  /** Foto: abre câmera ou captura o frame atual. */
  async takePhoto(): Promise<void> {
    if (!this.cameraActive) {
      await this.startCamera();
      return;
    }
    this.capturePhoto();
  }

  /** Captura um frame do vídeo e envia como imagem. */
  private capturePhoto(): void {
    const video = this.cameraPreview?.nativeElement;
    if (!video || !this.stream || video.readyState < 2) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    this.stopCamera();
    this.isUploading = true;
    this.imgUploadMessage = 'Processando imagem...';
    this.imgUploadError = false;
    this.cdr.detectChanges();
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.isUploading = false;
          this.imgUploadMessage = undefined;
          this.cdr.detectChanges();
          return;
        }
        this.uploadMediaBlob(blob, false);
      },
      'image/jpeg',
      0.9
    );
  }

  /** Vídeo: inicia ou para a gravação. */
  async recordVideo(): Promise<void> {
    if (this.isRecording) {
      this.stopRecording();
      return;
    }
    if (!this.cameraActive) {
      await this.startCamera();
      if (!this.cameraActive) return;
    }
    this.startRecording();
  }

  private startRecording(): void {
    if (!this.stream) return;
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';
    this.recordedChunks = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: mime });
    } catch {
      this.mediaRecorder = new MediaRecorder(this.stream);
    }
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recordedChunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => {
      const chunks = [...this.recordedChunks];
      const mime = this.mediaRecorder?.mimeType || 'video/webm';
      this.mediaRecorder = null;
      this.recordedChunks = [];
      this.stream?.getTracks().forEach(t => t.stop());
      this.stream = null;
      this.cameraActive = false;
      this.isRecording = false;
      const video = this.cameraPreview?.nativeElement;
      if (video) video.srcObject = null;
      if (chunks.length > 0) {
        this.isUploading = true;
        this.imgUploadMessage = 'Processando vídeo...';
        this.imgUploadError = false;
        this.cdr.detectChanges();
        const blob = new Blob(chunks, { type: mime });
        this.uploadMediaBlob(blob, true);
      }
    };
    this.mediaRecorder.start(1000);
    this.isRecording = true;
  }

  private stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  private uploadMediaBlob(blob: Blob, isVideo: boolean): void {
    if (!this.isUploading) {
      this.isUploading = true;
      this.uploadFileName = blob instanceof File ? (blob as File).name : (isVideo ? 'Vídeo.webm' : 'Imagem.jpg');
      this.uploadProgress = 0;
      this.imgUploadMessage = undefined;
      this.imgUploadError = false;
      this.cdr.detectChanges();
    }
    const key = this.reportEquipment || '';
    const call$ = this.data.serviceType === 'oem'
      ? this.oemDealerService.saveMedia(blob, this.vehicleId, key)
      : this.evaluationService.saveMedia(blob, this.vehicleId, key);
    call$.subscribe({
      next: (res) => {
        this.uploadProgress = 100;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.isUploading = false;
          this.uploadFileName = undefined;
          this.uploadProgress = 0;
          this.imgUploadMessage = undefined;
          if (res.success) {
            this.photosAndVideos.push({ url: res.url, isVideo: StringUtils.isVideoUrl(res.url) || isVideo });
            if (!this.galleryImages) this.galleryImages = [];
            this.galleryImages.push({ small: res.url, medium: res.url, big: res.url });
          } else {
            this.imgUploadError = true;
            this.imgUploadMessage = res.status;
          }
          this.cdr.detectChanges();
        }, 200);
      },
      error: () => {
        this.isUploading = false;
        this.uploadFileName = undefined;
        this.uploadProgress = 0;
        this.imgUploadError = true;
        this.imgUploadMessage = 'Erro ao enviar. Tente novamente.';
        this.cdr.detectChanges();
      }
    });
  }

  /** Mobile: arquivo capturado pela câmera nativa (foto ou vídeo) – usa HTTP direto. */
  onNativeCameraFileSelected(event: Event, isVideo: boolean): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    this.uploadMediaBlob(file, isVideo);
  }

  /** Anexar: mesmo fluxo do "Selecionar arquivo" – usa o uploader (barra e progresso iguais). */
  onAttachFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    if (this.uploaderRef) {
      this.uploaderRef.addToQueue([file]);
    } else {
      this.uploadMediaBlob(file, this.mediaType === 'video');
    }
  }

  private async initReportFileUploader() {
     const prefix = this.data.serviceType === 'oem'
    ? 'oemDealer'
    : 'evaluation'

    this.imgUploader$ = this.fileUploaderService.getInstance(
      {
        onAfterAddingFile: (item: FileItem) => {
          if (this.uploaderRef) {
            this.uploaderRef.uploadItem(item);
          }
        },
        onBeforeUploadItem: (item: FileItem) => {
          this.uploadFileName = item.file.name;
          this.isUploading = true;
          this.uploadProgress = 0;
          this.imgUploadMessage = undefined;
          this.imgUploadError = false;
          this.cdr.detectChanges();
        },
        onProgressItem: (item: FileItem, progress: number) => {
          this.uploadProgress = progress;
          this.cdr.detectChanges();
        },
        onCompleteItem: (fileItem: FileItem, response: FileUploadResult) => {
          this.isUploading = false;
          this.uploadFileName = undefined;
          this.uploadProgress = 0;
          if (response.success) {
            this.imgUploadMessage = undefined;
            this.photosAndVideos.push({
              url: response.url,
              isVideo: StringUtils.isVideoUrl(response.url) || this.mediaType === 'video'
            });
            if(!this.galleryImages){
              this.galleryImages = [];
            }
            this.galleryImages.push({
              small: response.url,
              medium: response.url,
              big: response.url
            });
          } else {
            this.imgUploadError = true;
            this.imgUploadMessage = response.status;
          }
          this.cdr.detectChanges();
          fileItem.remove();
        },
      },
      {
        url: `${environment.api}/${prefix}/saveMedia/${this.vehicleId}?reportItem=${encodeURIComponent(this.reportEquipment || '')}`,
        maxFileSize: 20 * 1024 * 1024, // 20MB
      }
    );
  }

  removeItem(url: string) {
    this.photosAndVideos = this.photosAndVideos.filter(item => item.url !== url);
    this.galleryImages = this.galleryImages.filter(img => img.small !== url && img.medium !== url && img.big !== url);
    this.cdr.detectChanges();
  }

  openPreview(index: number) {
    this.onlyPreviewGallery.openPreview(index);
  }

  /** URL para uso em src (remove #video para o recurso carregar). */
  getMediaSrc(url: string): string {
    return StringUtils.getMediaSrc(url);
  }
}
